import {Box, HStack, Show, Text} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {type InterestRecommendation} from '@dms/api/interest-recommendations';
import i18n from '@dms/i18n';

interface ScoreLevelProps {
  score: InterestRecommendation['score'];
  showTotal?: boolean;
}

export function ScoreLevel(props: ScoreLevelProps) {
  if (isNil(props.score)) {
    return (
      <Text size="small" color="secondary">
        {i18n.t('entity.interestRecommendations.score.insufficientData', {
          defaultValue: 'Not enough data',
        })}
      </Text>
    );
  }

  const display = match(props.score.level)
    .with('high', () => ({
      bars: 3,
      color: 'severity.success' as const,
      label: i18n.t('entity.task.priority.high'),
    }))
    .with('medium', () => ({
      bars: 2,
      color: 'severity.warning' as const,
      label: i18n.t('entity.task.priority.medium'),
    }))
    .with('low', () => ({
      bars: 1,
      color: 'text.tertiary' as const,
      label: i18n.t('entity.task.priority.low'),
    }))
    .exhaustive();

  return (
    <HStack spacing={1} align="center">
      <HStack spacing={1} align="flex-end">
        <Box width={1} height={1} backgroundColor={display.color} />
        <Box
          width={1}
          height={2}
          backgroundColor={display.bars >= 2 ? display.color : 'general.separator'}
        />
        <Box
          width={1}
          height={3}
          backgroundColor={display.bars >= 3 ? display.color : 'general.separator'}
        />
      </HStack>
      <Text size="small">{display.label}</Text>
      <Show when={props.showTotal}>
        <Text size="small" color="secondary">
          {props.score.total > 0 ? '+' : ''}
          {props.score.total}
        </Text>
      </Show>
    </HStack>
  );
}
